import { refKey } from './refs.js';
const BATCH_MS = 25;
const RETRY_UNAVAILABLE_MS = 30_000;
const PENDING = { status: 'pending' };
export class RefStore {
    host;
    o;
    states = new Map();
    queued = new Map();
    failedAt = new Map();
    reads = new Map();
    listeners = new Set();
    timer;
    stack = [];
    version = 0;
    constructor(host, o = {}) {
        this.host = host;
        this.o = o;
    }
    subscribe = (fn) => {
        this.listeners.add(fn);
        return () => {
            this.listeners.delete(fn);
        };
    };
    getVersion = () => this.version;
    emit() {
        this.version++;
        for (const l of this.listeners)
            l();
    }
    state(ref) {
        if (ref.kind === 'artifact')
            return { status: this.o.hasArtifact?.(ref.text) ? 'resolved' : 'missing' };
        return this.states.get(refKey(ref)) ?? PENDING;
    }
    /** Ask the machine about `ref` (batched); a no-op once answered. */
    want(ref) {
        if (ref.kind === 'artifact')
            return;
        const key = refKey(ref);
        const st = this.states.get(key);
        if (st && st.status !== 'unavailable')
            return;
        const now = (this.o.now ?? Date.now)();
        if (st?.status === 'unavailable' && now - (this.failedAt.get(key) ?? 0) < RETRY_UNAVAILABLE_MS)
            return;
        if (this.queued.has(key))
            return;
        this.queued.set(key, { id: key, kind: ref.kind, text: ref.text });
        this.timer ??= setTimeout(() => void this.flush(), BATCH_MS);
    }
    async flush() {
        this.timer = undefined;
        const batch = [...this.queued.values()].slice(0, 100);
        for (const c of batch)
            this.queued.delete(c.id);
        if (this.queued.size)
            this.timer = setTimeout(() => void this.flush(), BATCH_MS);
        if (!batch.length)
            return;
        try {
            const results = await this.host.resolve(batch);
            const byId = new Map(results.map((r) => [r.id, r]));
            for (const c of batch) {
                const r = byId.get(c.id);
                this.states.set(c.id, r ? { status: r.status, resolution: r } : { status: 'missing' });
            }
        }
        catch {
            const now = (this.o.now ?? Date.now)();
            for (const c of batch) {
                this.states.set(c.id, { status: 'unavailable' });
                this.failedAt.set(c.id, now);
            }
        }
        this.emit();
    }
    /** Open a detected ref in the inspector (resolved target or the chooser). */
    openRef(ref) {
        if (ref.kind === 'artifact')
            return this.push({ kind: 'artifact', artifactId: ref.text });
        const r = this.states.get(refKey(ref))?.resolution;
        const lines = {
            ...(ref.line ? { line: ref.line } : {}),
            ...(ref.lineEnd ? { lineEnd: ref.lineEnd } : {}),
        };
        if (r?.status === 'resolved' && r.target)
            this.push({ kind: 'target', target: r.target, ...lines, label: ref.text });
        else if (r?.status === 'ambiguous' && r.matches?.length)
            this.push({ kind: 'choose', label: ref.text, matches: r.matches, ...lines });
    }
    get current() {
        return this.stack.at(-1);
    }
    get depth() {
        return this.stack.length;
    }
    /** Drill in (directory entry, commit file, chooser pick). */
    push(item) {
        this.stack = [...this.stack, item];
        this.emit();
    }
    /** Replace the top (chooser → picked target keeps Back meaningful). */
    replace(item) {
        this.stack = [...this.stack.slice(0, -1), item];
        this.emit();
    }
    back() {
        this.stack = this.stack.slice(0, -1);
        this.emit();
    }
    close() {
        this.stack = [];
        this.emit();
    }
    /** Cached per target; `fresh` re-reads (refresh a snapshot, re-check a file). */
    read(target, fresh = false) {
        const key = JSON.stringify(target);
        let p = fresh ? undefined : this.reads.get(key);
        if (!p) {
            p = this.host.read(target);
            this.reads.set(key, p);
            p.catch(() => this.reads.delete(key));
        }
        return p;
    }
}
//# sourceMappingURL=ref-store.js.map